import {
  COMPANY_FEATURE_SETTING_FIELDS,
  REPORT_EXTRA_EXPORT_FORMATS,
} from "@/lib/constants/company-feature-settings";
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGE_CODES,
} from "@/lib/i18n/languages";

// The six per-company feature settings in the company setup form (system
// admin only, WP4). The form keeps what the company holds apart from what
// the admin changed: only changes are sent. A setting the company does not
// hold resolves on the API from the host profile ("Host default"). A held
// value returns to the host default as null, sent on company update only.

export const BOOLEAN_FEATURE_SETTINGS = [
  "gtoolExportEnabled",
  "hideMoreSolutions",
  "reportFrameworksFilterRequired",
  "languageDetectionEnabled",
];

export const FEATURE_SETTINGS_FORM_DEFAULTS = Object.freeze({
  featureSettingsStored: Object.freeze({}),
  featureSettingEdits: Object.freeze({}),
});

const isHeld = (value) => value !== undefined && value !== null;

const normalizeFormats = (value) =>
  REPORT_EXTRA_EXPORT_FORMATS.filter((format) => value.includes(format));

// A held value read the way the API reads it (explicit checks).
const readHeldValue = (field, value) => {
  if (BOOLEAN_FEATURE_SETTINGS.includes(field)) return value === true;
  if (field === "reportExportFormats") {
    return Array.isArray(value) ? normalizeFormats(value) : [];
  }
  return SUPPORTED_LANGUAGE_CODES.includes(value) ? value : DEFAULT_LANGUAGE;
};

// An edit is kept only if it is a value the API accepts for the field.
const isValidEdit = (field, value) => {
  if (BOOLEAN_FEATURE_SETTINGS.includes(field))
    return typeof value === "boolean";
  if (field === "reportExportFormats") {
    return (
      Array.isArray(value) &&
      value.every((format) => REPORT_EXTRA_EXPORT_FORMATS.includes(format))
    );
  }
  if (field === "defaultLanguage")
    return SUPPORTED_LANGUAGE_CODES.includes(value);
  return false;
};

const toPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

/** What the company document holds, per field (a stored null is not held). */
export const readStoredFeatureSettings = (company) => {
  const source = toPlainObject(company);
  const stored = {};
  for (const field of COMPANY_FEATURE_SETTING_FIELDS) {
    if (isHeld(source[field]))
      stored[field] = readHeldValue(field, source[field]);
  }
  return stored;
};

/**
 * Only valid edits of the six fields, formats in enum order. A null edit
 * clears a held value back to the host default (company update only).
 */
export const sanitizeFeatureSettingEdits = (
  edits,
  { allowClear = true } = {},
) => {
  const source = toPlainObject(edits);
  const clean = {};
  for (const field of COMPANY_FEATURE_SETTING_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(source, field)) continue;
    const value = source[field];
    if (value === null) {
      if (allowClear) clean[field] = null;
      continue;
    }
    if (!isValidEdit(field, value)) continue;
    clean[field] =
      field === "reportExportFormats" ? normalizeFormats(value) : value;
  }
  return clean;
};

export const normalizeFeatureSettingsFormValues = (form = {}) => ({
  featureSettingsStored: readStoredFeatureSettings(form?.featureSettingsStored),
  featureSettingEdits: sanitizeFeatureSettingEdits(form?.featureSettingEdits),
});

/** For the company form patch: the company's values, no edits. */
export const buildFeatureSettingsFormPatch = (company) => ({
  featureSettingsStored: readStoredFeatureSettings(company),
  featureSettingEdits: {},
});

/**
 * Create mode holds no company values. A previous company's stored settings
 * left in the form (and any edits made against them) are cleared, so the
 * controls offer "Host default" and nothing is implied. Null if clean.
 */
export const getCreateModeFeatureSettingsReset = (form) =>
  Object.keys(toPlainObject(form?.featureSettingsStored)).length > 0
    ? { featureSettingsStored: {}, featureSettingEdits: {} }
    : null;

/**
 * The request fields: the admin's valid edits only. Clears (null) only on a
 * company update; the API refuses null when a company is created.
 */
export const buildFeatureSettingsPayload = (
  edits,
  { allowClear = false } = {},
) => sanitizeFeatureSettingEdits(edits, { allowClear });

/**
 * Host defaults, known only from a company-less system admin's own resolved
 * user (the API resolves such a user from the host profile) holding all six.
 * A system admin with a company sees that company's values on their user,
 * not host defaults, so null.
 */
export const getCompanyFeatureDisplayDefaults = (user) => {
  if (user?.role !== "system_admin" || user?.companyId) return null;
  if (!COMPANY_FEATURE_SETTING_FIELDS.every((field) => isHeld(user[field]))) {
    return null;
  }
  return Object.fromEntries(
    COMPANY_FEATURE_SETTING_FIELDS.map((field) => [
      field,
      readHeldValue(field, user[field]),
    ]),
  );
};

const sameValue = (a, b) =>
  Array.isArray(a) && Array.isArray(b)
    ? normalizeFormats(a).join(",") === normalizeFormats(b).join(",")
    : a === b;

/**
 * The control state: an edit, else the held value, else "Host default". A
 * null edit is a held value being cleared back to the host default.
 */
export const getFeatureSettingState = ({ field, stored = {}, edits = {} }) => {
  const held = Object.prototype.hasOwnProperty.call(stored, field);
  // What the company holds, so a cleared control can offer it back.
  const storedValue = held ? stored[field] : undefined;
  if (Object.prototype.hasOwnProperty.call(edits, field)) {
    return edits[field] === null
      ? { mode: "default", value: undefined, held, storedValue, edited: true }
      : {
          mode: "explicit",
          value: edits[field],
          held,
          storedValue,
          edited: true,
        };
  }
  if (held) {
    return {
      mode: "explicit",
      value: storedValue,
      held,
      storedValue,
      edited: false,
    };
  }
  return {
    mode: "default",
    value: undefined,
    held,
    storedValue,
    edited: false,
  };
};

/**
 * Applies the admin's choice; `undefined` means "Host default". Returning to
 * the state the field started in removes the edit, so nothing is sent.
 */
export const applyFeatureSettingChoice = ({
  field,
  stored = {},
  edits = {},
  value,
}) => {
  const next = { ...edits };
  const held = Object.prototype.hasOwnProperty.call(stored, field);
  if (value === undefined) {
    // Host default: a held value is cleared (null, sent on update); a
    // setting the company does not hold already is the host default.
    if (held) next[field] = null;
    else delete next[field];
    return next;
  }
  if (!isValidEdit(field, value)) return next;
  const clean =
    field === "reportExportFormats" ? normalizeFormats(value) : value;
  if (held && sameValue(stored[field], clean)) delete next[field];
  else next[field] = clean;
  return next;
};
